/**
 * ScheduleChangeApprovals — Wave 3.2 live-API wrapper around <ApprovalList>.
 *
 * Backend data shape for type='schedule_change' (from seed_test_data.py):
 *   { from_schedule, to_schedule, effective_date, reason }
 */

import React, { useCallback, useMemo } from 'react';
import { Typography } from '@mui/material';
import ApprovalList, { ApprovalColumn, ApprovalFormField } from './ApprovalList';
import { useTranslation } from 'react-i18next';
import * as attendanceApi from '../../../api/attendance';

function pickStr(data: unknown, key: string): string {
  return typeof data === 'object' && data !== null && key in data
    ? String((data as Record<string, unknown>)[key] ?? '')
    : '';
}

const ScheduleChangeApprovals: React.FC = () => {
  const { t } = useTranslation();

  const columns = useMemo<ApprovalColumn[]>(() => [
    {
      label: t('attendance:approvals.scheduleChange.colEffectiveDate'),
      render: (r) => pickStr(r.data, 'date') || pickStr(r.data, 'effective_date') || '—',
    },
    {
      label: t('attendance:approvals.scheduleChange.colCurrentSchedule'),
      render: (r) => (
        <Typography variant="caption">
          {pickStr(r.data, 'from_shift') ||
            pickStr(r.data, 'from_schedule') ||
            pickStr(r.data, 'current_schedule') ||
            '—'}
        </Typography>
      ),
    },
    {
      label: t('attendance:approvals.scheduleChange.colRequestedSchedule'),
      render: (r) => (
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'primary.main' }}
        >
          {pickStr(r.data, 'to_shift') ||
            pickStr(r.data, 'to_schedule') ||
            pickStr(r.data, 'requested_schedule') ||
            '—'}
        </Typography>
      ),
    },
    {
      label: t('attendance:approvals.scheduleChange.colReason'),
      render: (r) => (
        <Typography variant="caption" color="text.secondary">
          {pickStr(r.data, 'reason') || '—'}
        </Typography>
      ),
    },
  ], [t]);

  // Both a KEY fix and a CONTROL fix, and the key half was the more serious one.
  //
  // This form wrote `effective_date` / `from_schedule` / `to_schedule`; the worker portal
  // and mobile write `date` / `from_shift` / `to_shift`. The backend reads `date` (and
  // never `effective_date`) — so the three columns above showed "—" for every request a
  // worker or a phone submitted, i.e. an admin could not see what was actually requested.
  //
  // And the two shift fields were free text, so an admin could record a shift name the
  // organization does not have. `timetablesApi.list()` is org-scoped and already the
  // source the Shifts page uses, driven through the `dynamic_select` loader that Leave
  // type has used since P1.7 — no new primitive (2026-08-25, owner report).
  const shiftOptionsLoader = useCallback(async () => {
    const timetables = await attendanceApi.timetablesApi.list();
    return timetables.map((tt) => ({
      // The ID is submitted, matching both other surfaces since 2026-08-25: an approved
      // change now ROSTERS the worker onto this timetable, and `timetables.name` has no
      // unique constraint, so a name cannot identify which one.
      value: tt.id,
      label: tt.clock_in && tt.clock_out
        ? `${tt.name} (${tt.clock_in}–${tt.clock_out})`
        : tt.name,
      // …and the bare name travels alongside as the snapshot the approver reads, so a
      // later rename cannot rewrite what was asked for. The label is not usable for that:
      // it carries the hours too.
      snapshot: tt.name,
    }));
  }, []);

  const createFields = useMemo<ApprovalFormField[]>(() => [
    { key: 'date', label: t('attendance:approvals.scheduleChange.colEffectiveDate'), type: 'date', required: true, span: 6 },
    {
      // RESOLVED, not chosen. It was a `dynamic_select` over every timetable in the org,
      // never checked against the worker — so an admin could record "from Standard Day"
      // for somebody on Early Shift, and nothing anywhere would catch it (the engine does
      // not read this key; it is what the approver is told). Not `required`, because "no
      // schedule that day" is a real answer and the old `required: true` blocked a request
      // the worker's own portal accepted. 2026-08-26 audit, F1/F2.
      key: 'from_shift_id', label: t('attendance:approvals.scheduleChange.colCurrentSchedule'),
      type: 'derived_current_shift', span: 6, derivedFromDates: ['date', 'date'],
    },
    {
      key: 'to_shift_id', label: t('attendance:approvals.scheduleChange.colRequestedSchedule'),
      type: 'dynamic_select', required: true, optionsLoader: shiftOptionsLoader,
      snapshotKey: 'to_shift',
    },
    { key: 'reason', label: t('attendance:approvals.scheduleChange.colReason'), type: 'textarea', required: true },
  ], [t, shiftOptionsLoader]);

  return (
    <ApprovalList
      type="schedule_change"
      title={t('attendance:approvals.scheduleChangeTitle')}
      description={t('attendance:approvals.scheduleChangeDescription')}
      recordTitle={t('attendance:approvals.scheduleChange.recordTitle')}
      filterLabel={t('attendance:approvals.filterLabel')}
      columns={columns}
      createFields={createFields}
    />
  );
};

export default ScheduleChangeApprovals;
